import { StackNavigationProp } from '@react-navigation/stack';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import { Subscription } from 'expo-modules-core';
import * as Notifications from 'expo-notifications';

import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Alert,
  AppState,
  AppStateStatus,
  Linking,
  Platform
} from 'react-native';
import { useDeviceId } from '../hooks/device/useDeviceId';
import useUpsertUserDevice from '../hooks/user/useUpsertUserDevice';
import { useUserContext } from './UserContext';
import { UserDevice } from '../models/UserDevice';
import AppManifest from '../common/AppManifest';
import { log } from '../tools/LogHelper';

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: true,
    shouldSetBadge: true
  })
});

export interface NotificationContextProps {
  userDevice?: UserDevice;
  token?: string;
  error?: string;
  notifications: Notifications.Notification[];
  registering?: boolean;
  register: () => void;
}

export const NotificationContext = createContext<NotificationContextProps>(
  null!
);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotificationContext must be used within the context of the NotificationProvider component'
    );
  }
  return context;
};

export const NotificationProvider: React.FC<
  PropsWithChildren & {
    navigation?: StackNavigationProp<any>;
  }
> = ({ navigation, children }) => {
  const [token, setToken] = useState<string>();
  const [error, setError] = useState('');
  const [registering, setRegistering] = useState(false);
  const [notifications, setNotifications] = useState<
    Notifications.Notification[]
  >([]);
  // const navigation = useNavigation<StackNavigationProp<any>>();
  const { user } = useUserContext();
  const { deviceId } = useDeviceId();
  const {
    userDevice,
    upsertingUserDevice,
    upsertingUserDeviceError,
    upsertUserDevice
  } = useUpsertUserDevice(user?.id || 0);
  // persist userDevice
  // useEffect(() => {
  //   (async () => {
  //     if (userDevice?.id) {
  //       if (Platform.OS === 'web') {
  //         await AsyncStorage.setItem('userDeviceId', userDevice.id.toString());
  //       } else {
  //         await SecureStore.setItemAsync(
  //           'userDeviceId',
  //           userDevice.id.toString()
  //         );
  //       }
  //     }
  //   })();
  // }, [userDevice]);
  const notificationListener = useRef<Subscription>(null!);
  const responseListener = useRef<Subscription>(null!);
  useEffect(() => {
    // This listener is fired whenever a notification is received while the app is foregrounded
    notificationListener.current =
      Notifications.addNotificationReceivedListener(notification => {
        // console.log('addNotificationReceivedListener', notification);
        setNotifications(notifications => [...notifications, notification]);
      });

    // This listener is fired whenever a user taps on or interacts with a notification (works when app is foregrounded, backgrounded, or killed)
    responseListener.current =
      Notifications.addNotificationResponseReceivedListener(response => {
        // console.log('addNotificationResponseReceivedListener', response);
        const data = response?.notification?.request?.content?.data;
        switch (data?.type) {
          case 'message':
            navigation?.navigate('HomeDrawerNav', {
              screen: 'BottomTabNav',
              params: {
                screen: 'ConversationsNav',
                params: {
                  screen: 'ConversationRoom',
                  params: { roomId: data.roomId }
                }
              }
            });
            break;
        }
      });

    return () => {
      notificationListener.current?.remove();
      responseListener.current?.remove();
    };
  }, [navigation]);

  const logUserDevice = useCallback(
    (expoPushToken?: string) => {
      if (!deviceId) return;
      const date = new Date();
      const utcOffset = date.getTimezoneOffset() / 60;
      upsertUserDevice({
        expoPushToken,
        deviceIdentifier: deviceId,
        isDevice: Device.isDevice,
        utcOffset,
        osName: Device.osName,
        osVersion: Device.osVersion,
        appVersion: AppManifest.version,
        appBuild: +(AppManifest.buildNumber || 0),
        deviceName: Device.deviceName,
        manufacturer: Device.manufacturer,
        modelName: Device.modelName,
        model: Device.modelId || Device.productName,
        deviceYearClass: Device.deviceYearClass
      });
    },
    [deviceId, upsertUserDevice]
  );

  const register = useCallback(async () => {
    let token;
    if (Platform.OS === 'android') {
      Notifications.setNotificationChannelAsync('default', {
        name: 'default',
        importance: Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: '#FF231F7C'
      });
    }
    setRegistering(true);
    if (Device.isDevice) {
      const { status: existingStatus } =
        await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;
      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }
      if (finalStatus !== 'granted') {
        Alert.alert(
          'Alert',
          'Please enable notifications for Chloe by navigating to the Settings app.',
          [
            {
              text: 'Settings',
              onPress: async () => {
                if (Platform.OS === 'android') {
                  await Linking.sendIntent(
                    'android.settings.APP_NOTIFICATION_SETTINGS',
                    [
                      {
                        key: 'android.provider.extra.APP_PACKAGE',
                        value: Constants.expoConfig?.android?.package || ''
                      }
                    ]
                  );
                } else {
                  await Linking.openSettings();
                }
              }
            },
            {
              text: 'Cancel'
            }
          ]
        );
        setRegistering(false);
        logUserDevice();
        return;
      }
      // add projectId if building using eas build local
      token = (await Notifications.getExpoPushTokenAsync()).data;
      log('push token', token);
    } else {
      // alert('Must use physical device for Push Notifications');
    }
    setToken(token);
    setRegistering(false);
    // console.log('token', token, deviceId);
    if (deviceId) {
      // send token to server
      logUserDevice(token);
    }
    return token;
  }, [deviceId, logUserDevice]);

  // Check if user has set notification settings
  const mountRef = useRef(false);
  const appState = useRef(AppState.currentState);
  useEffect(() => {
    if (Platform.OS === 'web' && user) {
      logUserDevice();
      return;
    }
    const checkForNotificationSetting = () => {
      if (
        user &&
        deviceId &&
        !mountRef.current
        // && typeof user.config?.isMessageNotification === 'undefined'
      ) {
        // console.log('registering');
        register();
        mountRef.current = true;
      }
    };
    // only check when app first starts
    checkForNotificationSetting();
    // Add check when entering foreground
    // const handleAppStateChange = async (nextAppState: AppStateStatus) => {
    //   if (
    //     appState.current.match(/inactive|background/) &&
    //     nextAppState === 'active'
    //   ) {
    //     // mountRef.current = false;
    //   }
    //   appState.current = nextAppState;
    // };
    // const subscription = AppState.addEventListener(
    //   'change',
    //   handleAppStateChange
    // );
    // return () => {
    //   subscription.remove();
    // };
  }, [user, deviceId, register, logUserDevice]);
  // check if user notification settings are set and if not update user config
  // const { updateUser } = useUpdateUser();
  // useEffect(() => {
  //   // updaate user.config after registering if user config not set
  //   if (
  //     user &&
  //     token &&
  //     userNotification &&
  //     typeof user.config?.isMessageNotification === 'undefined'
  //   ) {
  //     user.config = {
  //       isStatusUpdateNotification: true,
  //       isStatusUpdateEmailNotification: true,
  //       isDailyPromptNotification: true,
  //       isDailyPromptEmailNotification: true,
  //       isMessageNotification: true,
  //       isMessageEmailNotification: true
  //     };
  //     updateUser({ config: user.config });
  //   }
  // }, [token, user, userNotification, updateUser]);

  return (
    <NotificationContext.Provider
      value={{
        userDevice,
        token,
        error: error || upsertingUserDeviceError?.message,
        notifications,
        registering: registering || upsertingUserDevice,
        register
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
